import { defineMessages, MessageDescriptor } from 'react-intl';
import type { ReelVoiceId } from 'fym-shared/src/music24/reelVoices';

/** The copy of the AI reel form in the Studio (design `music24-ai-reels` frame a, release-kit n4). */
export const reelMessages = defineMessages({
  aiChip: { id: 'app.sml.promote.reels.aiChip', defaultMessage: 'AI reel · 9:16' },
  release: { id: 'app.sml.promote.reels.release', defaultMessage: 'Release' },
  song: { id: 'app.sml.promote.reels.song', defaultMessage: 'Song' },
  lyrics: { id: 'app.sml.promote.reels.lyrics', defaultMessage: 'Lyrics' },
  optional: { id: 'app.sml.promote.reels.optional', defaultMessage: 'Optional' },
  lyricsCount: { id: 'app.sml.promote.reels.lyrics.count', defaultMessage: '{count} / {max}' },
  lyricsHint: {
    id: 'app.sml.promote.reels.lyrics.hint',
    defaultMessage: 'One line per lyric line. With no lyrics, the AI listens for them.',
  },
  theme: { id: 'app.sml.promote.reels.theme', defaultMessage: 'Theme' },
  waveform: { id: 'app.sml.promote.reels.waveform', defaultMessage: 'Waveform' },
  waveformHint: {
    id: 'app.sml.promote.reels.waveform.hint',
    defaultMessage: 'The reel is AI footage. Its style option is the shape of the waveform.',
  },
  waveformStartsWith: {
    id: 'app.sml.promote.reels.waveform.startsWith',
    defaultMessage: '{theme} starts with {shape}.',
  },
  voiceover: { id: 'app.sml.promote.reels.voiceover', defaultMessage: 'Voiceover' },
  voiceoverHint: { id: 'app.sml.promote.reels.voiceover.hint', defaultMessage: 'Play a sample, then pick a voice' },
  voicePlay: { id: 'app.sml.promote.reels.voice.play', defaultMessage: 'Play the sample of {name}' },
  voiceStop: { id: 'app.sml.promote.reels.voice.stop', defaultMessage: 'Stop the sample of {name}' },
  voiceLine: { id: 'app.sml.promote.reels.voice.line', defaultMessage: '{gender} · {tone}' },
  voiceFemale: { id: 'app.sml.promote.reels.voice.female', defaultMessage: 'Female' },
  voiceMale: { id: 'app.sml.promote.reels.voice.male', defaultMessage: 'Male' },
  noVoiceover: { id: 'app.sml.promote.reels.voice.none', defaultMessage: 'No voiceover' },
  noVoiceoverHint: {
    id: 'app.sml.promote.reels.voice.none.hint',
    defaultMessage: 'The reel has the song and the captions only.',
  },
  length: { id: 'app.sml.promote.reels.length', defaultMessage: 'Length' },
  lengthHint: { id: 'app.sml.promote.reels.length.hint', defaultMessage: 'A short song gets a 15 s reel.' },
  seconds: { id: 'app.sml.promote.reels.seconds', defaultMessage: '{count} s' },
  railTitle: { id: 'app.sml.promote.reels.rail.title', defaultMessage: 'Your reel' },
  railSize: { id: 'app.sml.promote.reels.rail.size', defaultMessage: '{width} × {height}' },
  railPlaceholder: { id: 'app.sml.promote.reels.rail.placeholder', defaultMessage: 'The reel shows here' },
  railNotSet: { id: 'app.sml.promote.reels.rail.notSet', defaultMessage: 'Not set' },
  railVoice: { id: 'app.sml.promote.reels.rail.voice', defaultMessage: 'Voice' },
  generate: { id: 'app.sml.promote.reels.generate', defaultMessage: 'Generate reel' },
  generateHint: {
    id: 'app.sml.promote.reels.generate.hint',
    defaultMessage: 'It takes about 2 minutes. If it fails, you get the video credit back.',
  },
  unavailableTitle: {
    id: 'app.sml.promote.reels.unavailable.title',
    defaultMessage: 'AI reels are not available right now',
  },
  unavailableBody: {
    id: 'app.sml.promote.reels.unavailable.body',
    defaultMessage: 'We cannot make a reel at the moment. No video credit was used. Try again later.',
  },
  featureOffTitle: {
    id: 'app.sml.promote.reels.featureOff.title',
    defaultMessage: 'AI reels are not open for your workspace',
  },
  featureOffBody: {
    id: 'app.sml.promote.reels.featureOff.body',
    defaultMessage: 'Sign in again to open it. If this message stays, contact support.',
  },
});

interface ReelVoiceMessages {
  label: MessageDescriptor;
  tone: MessageDescriptor;
}

/** The name and the tone of each voice. Never show the `REEL_VOICES` label or tone. */
export const reelVoiceMessages: Record<ReelVoiceId, ReelVoiceMessages> = {
  kore: defineMessages({
    label: { id: 'app.sml.promote.reels.voice.kore', defaultMessage: 'Nova' },
    tone: { id: 'app.sml.promote.reels.voice.kore.tone', defaultMessage: 'firm, confident' },
  }),
  zephyr: defineMessages({
    label: { id: 'app.sml.promote.reels.voice.zephyr', defaultMessage: 'Skye' },
    tone: { id: 'app.sml.promote.reels.voice.zephyr.tone', defaultMessage: 'bright' },
  }),
  aoede: defineMessages({
    label: { id: 'app.sml.promote.reels.voice.aoede', defaultMessage: 'Rae' },
    tone: { id: 'app.sml.promote.reels.voice.aoede.tone', defaultMessage: 'breezy' },
  }),
  sulafat: defineMessages({
    label: { id: 'app.sml.promote.reels.voice.sulafat', defaultMessage: 'June' },
    tone: { id: 'app.sml.promote.reels.voice.sulafat.tone', defaultMessage: 'warm' },
  }),
  puck: defineMessages({
    label: { id: 'app.sml.promote.reels.voice.puck', defaultMessage: 'Jax' },
    tone: { id: 'app.sml.promote.reels.voice.puck.tone', defaultMessage: 'upbeat' },
  }),
  charon: defineMessages({
    label: { id: 'app.sml.promote.reels.voice.charon', defaultMessage: 'Cole' },
    tone: { id: 'app.sml.promote.reels.voice.charon.tone', defaultMessage: 'informative, deep' },
  }),
  achird: defineMessages({
    label: { id: 'app.sml.promote.reels.voice.achird', defaultMessage: 'Theo' },
    tone: { id: 'app.sml.promote.reels.voice.achird.tone', defaultMessage: 'friendly' },
  }),
  algenib: defineMessages({
    label: { id: 'app.sml.promote.reels.voice.algenib', defaultMessage: 'Rook' },
    tone: { id: 'app.sml.promote.reels.voice.algenib.tone', defaultMessage: 'gravelly' },
  }),
};
